import { useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { assetUrl } from '#/utils/assetUrl';
import { getIsAndroid } from '#/domains/landing/utils/inApp';
import ShareModal from './ShareModal';

/** Android renders the local SVG inconsistently in WebView; use the CDN icon there. */
const ANDROID_SHARE_ICON =
  'https://cdn.3o3.co.kr/mystique/icons/outline_share.svg';

interface ShareButtonProps {
  shareUrl?: string;
  /** GTM Click-ID trigger key — keep stable. */
  id?: string;
  className: string;
  iconSize: number;
  iconClassName?: string;
  ariaLabel?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/**
 * Share affordance: icon choice, open state and the modal, in one place.
 * Callers supply only their own layout via `className` / `children`.
 */
export default function ShareButton({
  shareUrl,
  id,
  className,
  iconSize,
  iconClassName,
  ariaLabel,
  style,
  children,
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const iconSrc = getIsAndroid()
    ? ANDROID_SHARE_ICON
    : assetUrl('/icon-share.svg');

  return (
    <>
      <button
        id={id}
        type="button"
        onClick={() => setIsOpen(true)}
        className={className}
        aria-label={ariaLabel}
        style={style}
      >
        <img
          src={iconSrc}
          alt=""
          width={iconSize}
          height={iconSize}
          className={iconClassName}
          draggable={false}
        />
        {children}
      </button>
      {/* Portalled: the sticky CTA bar is `-translate-x-1/2`, and a transform
          makes a containing block for `position: fixed` descendants — the
          overlay must escape it to cover the viewport. */}
      {createPortal(
        <ShareModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          shareUrl={shareUrl}
        />,
        document.body
      )}
    </>
  );
}
